import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint, faTimes } from '@fortawesome/free-solid-svg-icons';
import logo from '../assets/logo192.png';
import { getFormattedDate } from '../assets/Utils';
import '../styles/EmployeeSalaryStatement.css';

const formatMoney = (value) =>
  Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const EmployeeSalaryStatement = ({ summary, canViewPay = true, onClose }) => {
  const [payments, setPayments] = useState([]);
  const [leaveSummary, setLeaveSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!summary?.employee_id || !summary?.pay_period) {
      setError('This row is missing employee or pay period details.');
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;

    const fetchPayments = async () => {
      setIsLoading(true);
      setError('');
      try {
        const paymentResponse = await axios.get(
          `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/employees/payments`,
          {
            params: {
              employee_id: summary.employee_id,
              pay_period: summary.pay_period,
            },
          }
        );
        let leaveData = null;
        try {
          const leaveResponse = await axios.get(
            `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/employees/leave/statement`,
            {
              params: {
                employee_id: summary.employee_id,
                pay_period: summary.pay_period,
              },
            }
          );
          leaveData = leaveResponse.data;
        } catch (leaveError) {
          console.error('Error fetching leave statement:', leaveError);
        }
        if (cancelled) return;
        const rows = Array.isArray(paymentResponse.data) ? [...paymentResponse.data] : [];
        rows.sort((a, b) => new Date(a.payment_date) - new Date(b.payment_date));
        setPayments(rows);
        setLeaveSummary(leaveData);
      } catch (err) {
        console.error('Error fetching salary statement payments:', err);
        if (!cancelled) {
          setError('Could not load payments for this period.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchPayments();
    return () => {
      cancelled = true;
    };
  }, [summary]);

  useEffect(() => {
    const handleKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  const employee = payments[0]?.Employee || {};
  const employeeName = employee.employee_name || summary.employee_name || '';
  const branch = employee.branch || summary.branch || '';
  const role = employee.role || summary.role || '';
  const department = employee.department || summary.department || '';
  const contractedSalary = Number(employee.salary ?? summary.salary ?? 0);
  const salaryAmount = (value) => (canViewPay ? formatMoney(value) : '—');

  const totalsByReason = payments.reduce((acc, payment) => {
    const reason = payment.payment_reason || 'Other';
    acc[reason] = (acc[reason] || 0) + Number(payment.payment_amount || 0);
    return acc;
  }, {});

  const leaveAllowance = Number(totalsByReason['Leave Allowance'] || 0);
  const grandTotal = payments.reduce(
    (sum, payment) => sum + Number(payment.payment_amount || 0),
    0
  );
  const taxableFallback = Number(employee.base_pay);
  const exemptFallback = Boolean(employee.nassit_exempt);
  const fallbackEmployee = !exemptFallback && Number.isFinite(taxableFallback) && taxableFallback > 0
    ? Math.round(taxableFallback * 5) / 100
    : 0;
  const fallbackEmployer = !exemptFallback && Number.isFinite(taxableFallback) && taxableFallback > 0
    ? Math.round(taxableFallback * 10) / 100
    : 0;
  const nassitEmployee = leaveSummary ? Number(leaveSummary.nassit_employee || 0) : fallbackEmployee;
  const nassitEmployer = leaveSummary ? Number(leaveSummary.nassit_employer || 0) : fallbackEmployer;
  const netPay = grandTotal - nassitEmployee;
  const difference = grandTotal - leaveAllowance - contractedSalary;
  const leaveTaken = leaveSummary?.leave_taken || [];

  const handlePrint = () => {
    document.body.classList.add('salary-statement-printing');
    document.body.style.overflow = 'visible';
    const cleanup = () => {
      document.body.classList.remove('salary-statement-printing');
      document.body.style.overflow = 'hidden';
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    window.print();
  };

  return createPortal(
    <div className="salary-statement-overlay">
      <div className="salary-statement-backdrop no-print" onClick={onClose} />
      <div
        className="salary-statement"
        role="dialog"
        aria-modal="true"
        aria-labelledby="salary-statement-title"
      >
        <div className="salary-statement-actions no-print">
          <button
            type="button"
            className="salary-statement-print-button"
            onClick={handlePrint}
            disabled={isLoading}
          >
            <FontAwesomeIcon icon={faPrint} /> Print
          </button>
          <button type="button" className="salary-statement-close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} /> Close
          </button>
        </div>

        <div className="salary-statement-print-area">
          <div className="salary-statement-header">
            <img src={logo} alt="Shuman Hospital" className="salary-statement-logo" />
            <div>
              <h2 className="hospital-name">Shuman Hospital</h2>
              <p>15-17 Pultney Street, Freetown</p>
              <p>billing@shumanhospital.com</p>
              <p>Tel: (+232) 090 33 00 00</p>
            </div>
          </div>

          <h2 id="salary-statement-title" className="salary-statement-title">
            Employee Salary Statement
          </h2>

          {isLoading && <p className="salary-statement-status no-print">Loading payments...</p>}
          {error && <p className="salary-statement-error">{error}</p>}

          <div className="salary-statement-meta">
            <table className="salary-statement-info-table">
              <tbody>
                <tr>
                  <th>Employee</th>
                  <td>{employeeName}</td>
                </tr>
                {role ? (
                  <tr>
                    <th>Role</th>
                    <td>{role}</td>
                  </tr>
                ) : null}
                {department ? (
                  <tr>
                    <th>Department</th>
                    <td>{department}</td>
                  </tr>
                ) : null}
                <tr>
                  <th>Branch</th>
                  <td>{branch || '—'}</td>
                </tr>
                <tr>
                  <th>Pay Period</th>
                  <td>{summary.pay_period}</td>
                </tr>
              </tbody>
            </table>

            <table className="salary-statement-totals-table">
              <thead>
                <tr>
                  <th>Summary</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(totalsByReason).map(([reason, amount]) => (
                  <tr key={reason}>
                    <td>{reason}</td>
                    <td>{salaryAmount(amount)}</td>
                  </tr>
                ))}
                <tr>
                  <td>Number of Payments</td>
                  <td>{payments.length}</td>
                </tr>
                <tr>
                  <td>Contracted Salary</td>
                  <td>{salaryAmount(contractedSalary)}</td>
                </tr>
                <tr className="salary-statement-grand-total">
                  <td>Gross paid</td>
                  <td>{salaryAmount(grandTotal)}</td>
                </tr>
                <tr>
                  <td>NASSIT employee (5%)</td>
                  <td>{formatMoney(nassitEmployee)}</td>
                </tr>
                <tr>
                  <td>Net pay</td>
                  <td>{salaryAmount(netPay)}</td>
                </tr>
                <tr>
                  <td>NASSIT employer (10%, not deducted)</td>
                  <td>{formatMoney(nassitEmployer)}</td>
                </tr>
                <tr className={canViewPay && difference > 0.01 ? 'salary-statement-over' : ''}>
                  <td>Over / (Under) salary, excluding leave allowance</td>
                  <td>{salaryAmount(difference)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="salary-statement-title">Leave this period</h3>
          <table className="salary-statement-lines">
            <thead>
              <tr>
                <th>Type</th>
                <th>From</th>
                <th>To</th>
                <th>Days</th>
                <th>Unpaid days</th>
              </tr>
            </thead>
            <tbody>
              {leaveTaken.length === 0 ? (
                <tr>
                  <td colSpan="5">No approved leave in this pay period.</td>
                </tr>
              ) : leaveTaken.map((leave) => (
                <tr key={leave.request_id}>
                  <td>{leave.leave_type}{leave.prevented ? ' (prevented)' : ''}</td>
                  <td>{String(leave.start_date).slice(0, 10)}</td>
                  <td>{String(leave.end_date).slice(0, 10)}</td>
                  <td>{leave.working_days}</td>
                  <td>{leave.unpaid_days}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <table className="salary-statement-lines">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Paid Through</th>
                <th>Check #</th>
                <th>Note</th>
                <th className="amount-col">Amount</th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 && !isLoading && !error ? (
                <tr>
                  <td colSpan="6">No payments found for this period.</td>
                </tr>
              ) : (
                payments.map((payment) => (
                  <tr key={payment.payment_id}>
                    <td>{payment.payment_date ? getFormattedDate(payment.payment_date) : '—'}</td>
                    <td>{payment.payment_reason || '—'}</td>
                    <td>{payment.PaidThroughAccount?.account_name || payment.paid_through_account || '—'}</td>
                    <td>{payment.check_number || '—'}</td>
                    <td>{payment.note || ''}</td>
                    <td className="amount-col">{salaryAmount(payment.payment_amount)}</td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="5">Total</td>
                <td className="amount-col">{salaryAmount(grandTotal)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EmployeeSalaryStatement;
